<template>
    <div class="details-view">
        <div id="media-container">
            <div id="media-loading">
                <md-progress-spinner
                    v-if="loading"
                    md-mode="indeterminate"
                ></md-progress-spinner>
            </div>

            <audio-player
                v-if="program.type === 'audio'"
                v-show="!loading"
                :id="program.id"
                :poster="program.fhdImage"
                :settings="program"
            />
            <video-player
                v-else
                v-show="!loading"
                :id="program.id"
                :group-id="groupId"
                :poster="program.fhdImage"
                :settings="program"
            />

            <a class="back-button" title="Back" @click.prevent="goBack">
                <div class="playbutton">
                    <svgicon
                        class="mirror"
                        name="arrow_forward"
                        width="4rem"
                        height="4rem"
                        color="#ffffff"
                    ></svgicon>
                </div>
                <span class="button-text">BACK</span>
            </a>
        </div>
        <div v-if="showDetails" class="details">
            <div v-if="allowDonations" id="donate-cta">
                <div id="donate-message">{{ donateMessage }}</div>
                <button
                    id="donate-button"
                    class="btn btn-filled small-btn highlight-btn"
                    @click="startDonationProcess"
                >
                    {{ donateButtonText }}
                </button>
            </div>

            <div id="details-pane">
                <h1 class="title">{{ program.title }}</h1>
                <h2 class="sub-title">{{ program.subTitle }}</h2>
                <div class="video-info">
                    <h6
                        v-if="showVideoViews"
                        class="video-views video-info-item"
                    >
                        {{ program.views }}
                        {{ program.type === 'audio' ? 'listens' : 'views' }}
                    </h6>
                    <span v-if="showVideoViews && showVideoDate" class="dot"
                        >•</span
                    >
                    <h6 v-if="showVideoDate" class="video-date video-info-item">
                        {{ formattedDate }}
                    </h6>
                    <span
                        v-if="
                            showVideoViews &&
                            showVideoDate &&
                            !program.scheduleId &&
                            !program.live
                        "
                        class="dot"
                        >•</span
                    >
                    <h6
                        v-if="!program.scheduleId && !program.live"
                        class="time video-info-item"
                    >
                        {{ formattedTime }}
                    </h6>
                </div>
                <!-- eslint-disable-next-line -->
                <p class="description" v-html="safeDescription" />
            </div>
            <div id="disqus-pane">
                <Disqus
                    v-if="!!disqusShortname && $gideo.subscriber"
                    :page-config="disqusConfig"
                    :shortname="disqusShortname"
                />
            </div>
        </div>
        <div
            v-if="displayType === 'row' && nonEmptyGroups.length"
            class="row-container"
        >
            <ProgramGroupRow
                v-for="(group, i) in nonEmptyGroups"
                :key="i"
                :group-id="group.id"
                :has-user-suggestions="group.hasUserSuggestions"
                :title="group.title"
            />
        </div>
        <ProgramGroupGrid
            v-else-if="filteredItems.length"
            :items="filteredItems"
            :has-user-suggestions="program.hasUserSuggestions"
        />
    </div>
</template>

<script>
import moment from 'moment';
import 'moment-duration-format';

import VideoPlayer from '@/components/VideoPlayer.vue';
import AudioPlayer from '@/components/AudioPlayer.vue';
import ProgramGroupRow from '@/components/ProgramGroupRow.vue';
import ProgramGroupGrid from '@/components/ProgramGroupGrid.vue';
import { Disqus } from 'vue-disqus';

export default {
    name: 'DetailsView',
    components: {
        VideoPlayer,
        AudioPlayer,
        ProgramGroupRow,
        ProgramGroupGrid,
        Disqus,
    },
    props: {
        programId: {
            type: String,
            default: '',
        },
        groupId: {
            type: String,
            default: '',
        },
    },
    data: () => ({
        program: {},
        programGroups: [],
        programItems: [],
        showVideoDate: false,
        showVideoViews: false,
        allowDonations: false,
        donateButtonText: null,
        donateMessage: null,
        loading: true,
        disqusShortname: undefined,
        disqusConfig: {
            url: undefined,
            identifier: undefined,
            title: undefined,
        },
    }),
    computed: {
        showDetails: function () {
            return !!(
                this.program.type !== 'audio' ||
                this.program.description ||
                this.showVideoDate ||
                this.showVideoViews ||
                this.disqusShortname ||
                this.allowDonations
            );
        },
        safeDescription: function () {
            let description =
                this.program?.webDescription || this.program?.description || '';
            return description.replace(/<\s*\/*\s*(script)([^>]*)>/gim, '');
        },
        formattedTime: function () {
            if (this.program.length > 60) {
                return moment
                    .duration(this.program.length, 'seconds')
                    .format('h[h] m[m]');
            }

            return moment
                .duration(this.program.length, 'seconds')
                .format('s[s]');
        },
        formattedDate: function () {
            return moment(this.program.added).format('ll');
        },
        filteredItems: function () {
            //remove this program item from the list
            return this.programItems.filter(
                (item) => item.id !== this.programId,
            );
        },
        nonEmptyGroups: function () {
            return (
                this.programGroups &&
                this.programGroups.filter(
                    (group) => group.childCount > 0 || group.hasUserSuggestions,
                )
            );
        },
        displayType: function () {
            // display rows if there is more than one group
            // otherwise for no groups, items are displayed as a grid
            // and for one group, that group is displayed as a grid
            if (this.nonEmptyGroups && this.nonEmptyGroups.length > 1) {
                return 'rows';
            } else {
                return 'grid';
            }
        },
    },
    watch: {
        programId: function () {
            this.loadProgram();
        },
        groupId: function () {
            this.loadGroup();
        },
    },
    async mounted() {
        await Promise.all([this.loadProgram(), this.loadGroup()]);
    },
    async created() {
        const settings = await this.$appSettings;

        this.allowDonations = settings.allowDonations;
        this.donateButtonText = settings.donateButtonText || 'Donate';
        this.donateMessage = settings.donateMessage || 'Donate Now';
        /* Disqus support */
        this.disqusShortname = settings.disqusShortname;
        /* end Disqus support */
        this.showVideoDate = !!settings.showVideoDate;
        this.showVideoViews = !!settings.showVideoViews;
    },
    methods: {
        goBack() {
            this.$router.go(-1);
        },
        startDonationProcess() {
            this.$gideo.bus.$emit('donate-open');
        },
        configureDisqus() {
            this.disqusConfig.url = global.location.href;
            this.disqusConfig.identifier = `${this.groupId}|${this.programId}`;
            this.disqusConfig.title = this.program.title;
        },
        async loadGroup() {
            if (!this.groupId) {
                this.programItems = [];
                this.programGroups = [];
                return;
            }

            try {
                let programItems = await this.$gideo.getProgramGroups(
                    this.groupId,
                );
                this.programItems = programItems.data;
                this.programGroups = this.programItems.filter(
                    (group) => group.type === 'group',
                );

                /* Disqus support */
                this.configureDisqus();
            } catch (e) {
                this.programItems = [];
                this.programGroups = [];
            }
        },
        async loadProgram() {
            this.loading = true;
            if (!this.programId) {
                this.$router.replace({
                    name: 'PageNotFound',
                    params: [this.$route.path],
                });
                return;
            }
            try {
                let program = await this.$gideo.getProgram(this.programId);
                this.program = program.data;

                /* Disqus support */
                this.configureDisqus();
            } catch (e) {
                if (e.response && e.response.status === 404) {
                    this.$router.replace({
                        name: 'PageNotFound',
                        params: [this.$route.path],
                    });
                } else {
                    window.console.error(e);
                }
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss">
@use 'sass:math';
@import '@/assets/css/_variables.scss';
#media-container {
    .body--light & a.back-button {
        color: $body-color-light;
        .playbutton {
            border-color: $body-color-light;
            svg path {
                fill: $body-color-light;
            }
        }
        span {
            color: $body-color-light;
            transition: color ease 0.4s;
        }
    }
}
</style>
<style lang="scss" scoped>
@use 'sass:math';
@use 'sass:color';
@import '@/assets/css/_variables.scss';
#vid-preview {
    width: 100%;
    height: 0;
    padding-bottom: math.div(1080, 1920) * 100%;
    background-position: center center;
    background-size: contain;
    background-repeat: no-repeat;

    &::after {
        content: '';
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        background-color: rgba(#000, 0.5);
    }
}
#media-container {
    background-color: #000;
    position: relative;
    min-height: 15rem;

    .body--light & {
        border-color: color.scale($background-color-light, $lightness: -35%);
        background-color: color.scale(
            $background-color-light,
            $lightness: -6.5%
        );
    }

    @media (min-width: 1280px) {
        padding: 0 12%;
        min-height: 25rem;
    }
    #media-loading {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 1;
    }
    .back-button {
        display: none;
        cursor: pointer;
        text-decoration: none;
        position: absolute;
        top: 6%;
        left: 6%;
        transform: translate(-50%);
        color: inherit;

        @media (min-width: 1280px) {
            display: block;
        }
        svg {
            transform: rotate(180deg);
        }
        .button-text {
            display: block;
            font-size: 1.3rem;
            margin-top: 0.5em;
        }
        .playbutton {
            transition: ease 0.3s;
        }
        &:hover {
            color: #448aff;
            .playbutton {
                transform: translate(-5%);
            }
        }
    }
}
#donate-cta {
    border: 0.1em solid #777;
    border-radius: 0.1em;
    line-height: 1.5em;
    overflow: auto;
    padding: 0.5em;
    background-color: #555;

    .body--light & {
        border-color: color.scale($background-color-light, $lightness: -35%);
        background-color: color.scale(
            $background-color-light,
            $lightness: -6.5%
        );
    }

    button {
        float: right;
        background-color: #448aff;
        border-color: #448aff;
        color: #fff !important;
        &:hover,
        &:active {
            background-color: #448aff;
            opacity: 1;
        }
        @media (min-width: 1080px) {
            opacity: 0.8;
        }
    }
    #donate-message {
        float: left;
    }
}
.details {
    padding: 2rem 2rem;
    text-align: left;
    border-bottom: 0.1rem solid;
    border-color: color.scale($background-color, $lightness: 20%);
    font-size: 1.2rem;
    background-color: $background-color;
    display: grid;
    column-gap: 1em;
    row-gap: 1em;
    .body--light & {
        border-color: color.scale($background-color-light, $lightness: -35%);
        background-color: $background-color-light;
    }

    @media (min-width: 1080px) {
        font-size: 1.4rem;
        padding: 5rem 10rem;
        grid-template: auto auto / 50% 50%;
    }

    .title {
        font-size: 2em;
        font-weight: 500;
        line-height: 1.1em;
        margin: 0 0 0.24em;
    }
    .sub-title {
        font-size: 1.6em;
        font-weight: 500;
        line-height: 1.1em;
        text-align: left;
        margin: 0.5em 0 0.3em;
    }
    .video-info {
        text-align: left;
    }
    .video-info-item {
        font-size: 1em;
        font-weight: 400;
        line-height: 1.1em;
        margin: 0 0.5em 0.3em;
        display: inline-block;
        opacity: 0.7;
        &:first-child {
            margin-left: 0;
        }
    }

    .dot {
        font-size: 1em;
        line-height: 1.1em;
        opacity: 0.7;
    }

    .description {
        font-size: 1em;
        line-height: 1.3em;
        margin-bottom: 0;
    }

    #details-pane {
        @media (min-width: 1080px) {
            grid-column: 1;
            grid-row: 1 / last;
        }
    }
}
</style>
